import { hasPermission } from '@hq/permission';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ApiError,
  type AuditLogRow,
  type RegionOptionRow,
  type TenantAdminRow,
  type TenantCenterRow,
  type TenantCreateRequest,
  type TenantMetricRow,
  type TenantRow,
  type TenantSequencePreview,
  type TenantStatus,
  type TenantUpdateRequest,
  type TplRow
} from '@hq/api-sdk';
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Divider,
  Drawer,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Progress,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { checkShortName, formatDisplayDate, formatDisplayTime } from '@hq/utils';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import { LogoField, RegionAddressFields, TipText, sumWidth } from '@hq/ui-kit';
import { useNavigate } from 'react-router-dom';
import { hq, useAuthStore } from '../session';
import Regions from './Regions';

const STATUS_META: Record<TenantStatus, { color: string; label: string }> = {
  INIT: { color: 'blue', label: '待开通' },
  RUNNING: { color: 'green', label: '运行中' },
  SUSPEND: { color: 'orange', label: '已停用' },
  EXPIRED: { color: 'red', label: '已到期' }
};

interface TenantFormValues {
  tenantName?: string;
  regionCode?: string;
  superviseOrgId?: string;
  superviseOrgName?: string;
  contractNo?: string;
  serviceRange?: [Dayjs, Dayjs];
  maxOrgCount?: number;
  maxUserCount?: number;
  maxOpsCenter?: number;
  storageQuotaMb?: number;
  remark?: string;
  centerName?: string;
  /** 运营中心简称（2–6 个汉字）。 */
  centerShortName?: string;
  centerLogoUrl?: string;
  centerType?: 'REGION' | 'HYBRID' | 'ORG';
  /** 运营仓地址（省/市/区由 RegionAddressFields 写入）。 */
  regionCodes?: string[];
  provinceCode?: string;
  cityCode?: string;
  districtCode?: string;
  address?: string;
  lng?: number | null;
  lat?: number | null;
  /** 运营端（OPS）管理员：必填，开通后即可登录运营端展开管理。账号由系统生成后回显。 */
  adminRealName?: string;
  adminMobile?: string;
  adminPassword?: string;
  roleTemplates?: string[];
  approvalTemplates?: string[];
  dictTemplates?: string[];
  numberRuleTemplates?: string[];
}

/** 运营管理员弹窗表单（编辑时 password 留空表示不改；账号由系统生成，不在表单内）。 */
interface TenantAdminFormValues {
  realName?: string;
  mobile?: string;
  password?: string;
  opsCenterId?: string;
  status?: 'ENABLED' | 'DISABLED';
}

/** 运营中心弹窗表单（新增/维护，`sys:opscenter:manage`）。 */
interface CenterFormValues {
  centerName?: string;
  centerShortName?: string;
  leaderName?: string;
  leaderPhone?: string;
  address?: string;
  status?: 'ACTIVE' | 'INACTIVE';
}

/** 租户详情抽屉 Tab 键。 */
type DetailTab = 'orgs' | 'centers' | 'sequences' | 'admins' | 'quota';

/** 服务端分页每页条数（后端缺省 500 条全量返回，超过即静默截断，故前端必须显式带分页）。 */
const PAGE_SIZE = 20;

/** 租户管理：开通 / 编辑 / 停用恢复 / 机构核对，以及行政区划字典维护。 */
export default function Tenants() {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const startEnter = useAuthStore((state) => state.startEnter);
  const profile = useAuthStore((state) => state.profile);
  const queryClient = useQueryClient();
  const isAdmin = profile?.level === 'admin';
  // 后端把租户写操作拆成两档权限：开通/编辑/运营中心=manage，停用恢复/续约=lifecycle。
  // 只读账号（sys:tenant:view）此前能看到并点到这些按钮，点了必然 403。
  const canManage = isAdmin || (profile?.perms ? hasPermission(profile.perms, 'sys:tenant:manage') : false);
  const canLifecycle = isAdmin || (profile?.perms ? hasPermission(profile.perms, 'sys:tenant:lifecycle') : false);
  // 运营中心读写走独立权限码（TenantAdmin.SaveCenterAsync 校验 sys:opscenter:manage）
  const canOpsCenter = isAdmin || (profile?.perms ? hasPermission(profile.perms, 'sys:opscenter:manage') : false);
  // 最近操作日志走 AuditQuery.EnsureAuditPerm（两个码任一）：无权限就不发请求，
  // 否则页签一打开就固定显示一条 403 报错，看起来像系统故障。
  const canViewAudit =
    isAdmin || (profile?.perms ? hasPermission(profile.perms, ['sys:audit:view', 'sys:platform:audit'], 'any') : false);

  const [keyword, setKeyword] = useState('');
  const [status, setStatus] = useState<TenantStatus | ''>('');
  /** 服务端分页页码（1 起）；筛选条件变化时回到第 1 页。 */
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<TenantRow | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [checking, setChecking] = useState<TenantRow | null>(null);
  /** 租户详情抽屉的当前 Tab：机构核对 / 运营中心 / 运营管理员 / 配额与日志 */
  const [detailTab, setDetailTab] = useState<DetailTab>('orgs');
  const [adminOpen, setAdminOpen] = useState(false);
  const [adminEditing, setAdminEditing] = useState<TenantAdminRow | null>(null);
  const [adminSaving, setAdminSaving] = useState(false);
  const [adminForm] = Form.useForm<TenantAdminFormValues>();
  const [submitting, setSubmitting] = useState(false);
  // 续约：目标租户 + 新的到期日（docs/07 §7.4 renew）
  const [renewTarget, setRenewTarget] = useState<TenantRow | null>(null);
  const [renewEnd, setRenewEnd] = useState<Dayjs | null>(null);
  const [renewing, setRenewing] = useState(false);
  // 运营中心维护（租户详情 → 运营中心 Tab）
  const [centerOpen, setCenterOpen] = useState(false);
  const [centerEditing, setCenterEditing] = useState<TenantCenterRow | null>(null);
  const [centerSaving, setCenterSaving] = useState(false);
  const [centerForm] = Form.useForm<CenterFormValues>();
  const [form] = Form.useForm<TenantFormValues>();
  // 只有填写了运营中心名称，才展开 LOGO / 省市区 / 地址（运营仓定位）等必填项
  const hasCenter = Boolean(Form.useWatch('centerName', form));

  const query = useQuery({
    queryKey: ['tenants', keyword, status, page],
    queryFn: () =>
      hq.tenant.list({
        keyword: keyword || undefined,
        status: status || undefined,
        page,
        pageSize: PAGE_SIZE
      })
  });
  // 后端列表不带 total（兼容全量返回的旧调用），以「本页是否满页」判断是否还有下一页
  const tenantRows = query.data ?? [];
  const hasNextPage = tenantRows.length >= PAGE_SIZE;

  // 用轻量 options 接口：全量 regions() 会拉全国约 3200 行，这里只喂一个下拉
  const regions = useQuery({ queryKey: ['regions', 'options'], queryFn: () => hq.tenant.regionOptions() });

  // 模板清单只在"开通租户"抽屉打开时拉取（编辑租户不需要，也避免无谓的跨模块调用）
  const templateQueriesEnabled = formOpen && !editing;
  const roleTplQuery = useQuery({
    queryKey: ['platform-tpl', 'ROLE'],
    queryFn: () => hq.platform.tpl.list('ROLE'),
    enabled: templateQueriesEnabled,
    retry: false
  });
  const approvalTplQuery = useQuery({
    queryKey: ['platform-tpl', 'APPROVAL'],
    queryFn: () => hq.platform.tpl.list('APPROVAL'),
    enabled: templateQueriesEnabled,
    retry: false
  });
  const dictTplQuery = useQuery({
    queryKey: ['platform-tpl', 'DICT'],
    queryFn: () => hq.platform.tpl.list('DICT'),
    enabled: templateQueriesEnabled,
    retry: false
  });
  const numberRuleTplQuery = useQuery({
    queryKey: ['platform-tpl', 'NUMBER_RULE'],
    queryFn: () => hq.platform.tpl.list('NUMBER_RULE'),
    enabled: templateQueriesEnabled,
    retry: false
  });

  const detail = useQuery({
    queryKey: ['tenant-orgs', checking?.id],
    queryFn: () => hq.tenant.orgs(checking!.id),
    enabled: Boolean(checking)
  });

  // 运营端（OPS）管理员：租户详情抽屉第二个 Tab，开通后在此增补/重置
  const admins = useQuery({
    queryKey: ['tenant-admins', checking?.id],
    queryFn: () => hq.tenant.admins(checking!.id),
    enabled: Boolean(checking)
  });

  // 运营中心：开通向导只建 1 个，后续可在此增补/维护多个（C9/C4）
  const centersQuery = useQuery({
    queryKey: ['tenant-centers', checking?.id],
    queryFn: () => hq.tenant.centers(checking!.id),
    enabled: Boolean(checking) && detailTab === 'centers'
  });

  // 配额用量 + 最近操作日志（C10）：日志接口自带租户过滤，平台账号可跨租户查
  const metricQuery = useQuery({
    queryKey: ['tenant-metric', checking?.id],
    queryFn: () => hq.tenant.tenantMetric(checking!.id),
    enabled: Boolean(checking) && detailTab === 'quota'
  });
  // 编号规则预览（M5）：按租户当前模板推算下一条样号，不占号
  const sequenceQuery = useQuery({
    queryKey: ['tenant-sequences', checking?.id],
    queryFn: () => hq.tenant.sequences(checking!.id),
    enabled: Boolean(checking) && detailTab === 'sequences'
  });
  const tenantLogs = useQuery({
    queryKey: ['tenant-audit', checking?.id],
    queryFn: () => hq.audit.list({ tenantId: checking!.id, size: 10 }),
    enabled: Boolean(checking) && detailTab === 'quota' && canViewAudit,
    retry: false
  });

  const regionOptions = useMemo(
    () =>
      (regions.data ?? []).map((region: RegionOptionRow) => ({
        value: region.regionCode,
        label: `${region.regionName}（${region.regionCode}）`
      })),
    [regions.data]
  );

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ maxOrgCount: 50, maxUserCount: 500, maxOpsCenter: 1, storageQuotaMb: 10240 });
    setFormOpen(true);
  };

  // 监管单位候选：编辑租户时从该租户名下机构里选，不让运营手抄机构 ID
  const superviseOrgs = useQuery({
    queryKey: ['sys', 'tenant', editing?.id, 'org-options'],
    enabled: editing !== null,
    staleTime: 60_000,
    queryFn: () => hq.org.options({ tenantId: editing?.id })
  });

  const openEdit = (row: TenantRow) => {
    setEditing(row);
    form.setFieldsValue({
      tenantName: row.tenantName,
      superviseOrgId: row.superviseOrgId,
      contractNo: row.contractNo ?? undefined,
      serviceRange:
        row.serviceBegin && row.serviceEnd ? [dayjs(row.serviceBegin), dayjs(row.serviceEnd)] : undefined,
      maxOrgCount: row.maxOrgCount,
      maxUserCount: row.maxUserCount,
      maxOpsCenter: row.maxOpsCenter,
      storageQuotaMb: row.storageQuotaMb,
      remark: row.remark ?? undefined
    });
    setFormOpen(true);
  };

  const submit = async (values: TenantFormValues) => {
    setSubmitting(true);
    try {
      if (editing) {
        const body: TenantUpdateRequest = {
          tenantName: values.tenantName,
          superviseOrgId: values.superviseOrgId,
          contractNo: values.contractNo,
          serviceBegin: values.serviceRange?.[0]?.format('YYYY-MM-DD'),
          serviceEnd: values.serviceRange?.[1]?.format('YYYY-MM-DD'),
          maxOrgCount: values.maxOrgCount,
          maxUserCount: values.maxUserCount,
          maxOpsCenter: values.maxOpsCenter,
          storageQuotaMb: values.storageQuotaMb,
          remark: values.remark
        };
        await hq.tenant.update(editing.id, body);
        message.success('租户已更新');
      } else {
        const body: TenantCreateRequest = {
          // 租户编码由服务端按行政区划代码生成，前端不提交
          tenantName: values.tenantName,
          regionCode: values.regionCode,
          superviseOrgName: values.superviseOrgName,
          contractNo: values.contractNo,
          serviceBegin: values.serviceRange?.[0]?.format('YYYY-MM-DD'),
          serviceEnd: values.serviceRange?.[1]?.format('YYYY-MM-DD'),
          maxOrgCount: values.maxOrgCount,
          maxUserCount: values.maxUserCount,
          maxOpsCenter: values.maxOpsCenter,
          storageQuotaMb: values.storageQuotaMb,
          remark: values.remark,
          centers: values.centerName
            ? [
                {
                  // 运营中心编码由服务端按「租户编码-OC-序号」生成，前端不提交
                  centerName: values.centerName,
                  centerShortName: values.centerShortName,
                  logoUrl: values.centerLogoUrl,
                  centerType: values.centerType ?? 'REGION',
                  provinceCode: values.provinceCode,
                  cityCode: values.cityCode,
                  districtCode: values.districtCode,
                  address: values.address,
                  lng: values.lng ?? null,
                  lat: values.lat ?? null
                }
              ]
            : undefined,
          admin: {
            // 登录账号由服务端按「机构号 + 4 位序号」生成，前端不提交
            realName: values.adminRealName?.trim() || undefined,
            mobile: values.adminMobile?.trim() || undefined,
            password: values.adminPassword
          },
          templates:
            values.roleTemplates?.length ||
            values.approvalTemplates?.length ||
            values.dictTemplates?.length ||
            values.numberRuleTemplates?.length
              ? {
                  roles: values.roleTemplates ?? [],
                  approvals: values.approvalTemplates ?? [],
                  dicts: values.dictTemplates ?? [],
                  numberRules: values.numberRuleTemplates ?? []
                }
              : undefined
        };
        const result = await hq.tenant.create(body);
        const appliedRoles = result.templates?.roles.length ?? 0;
        const appliedFlows = result.templates?.approvals.length ?? 0;
        const appliedDicts = result.templates?.dicts.length ?? 0;
        const appliedSeqs = result.templates?.numberRules.length ?? 0;
        const skipped = result.templates?.skippedPerms ?? [];
        const appliedTotal = appliedRoles + appliedFlows + appliedDicts + appliedSeqs;
        // 管理员账号由系统生成，只在开通结果里出现这一次：用常驻弹窗回显，由用户手工关闭
        Modal.success({
          title: '租户已开通',
          width: 520,
          okText: '我已记录，关闭',
          content: (
            <div style={{ lineHeight: 1.9 }}>
              <div>
                租户：{result.tenantCode ?? '-'} · {values.tenantName}
              </div>
              <div>
                运营管理员登录账号：
                <span style={{ fontFamily: 'monospace', fontWeight: 600, fontSize: 15 }}>
                  {result.adminAccount ?? '-'}
                </span>
              </div>
              <div style={{ color: 'rgba(0,0,0,0.45)' }}>
                该账号仅本次回显，请立即记录并转交；口令为提交时填写的初始口令。
              </div>
              {appliedTotal > 0 ? (
                <div style={{ color: 'rgba(0,0,0,0.45)' }}>
                  套用模板：角色 {appliedRoles} / 审批流 {appliedFlows} / 字典 {appliedDicts} / 编号规则{' '}
                  {appliedSeqs}
                </div>
              ) : null}
            </div>
          )
        });
        if (skipped.length > 0) {
          message.warning(`模板中以下权限码不存在，已跳过：${skipped.join(', ')}`);
        }
      }
      setFormOpen(false);
      await queryClient.invalidateQueries({ queryKey: ['tenants'] });
    } catch (err) {
      message.error(err instanceof ApiError ? err.friendlyMessage : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const changeStatus = async (row: TenantRow, action: 'suspend' | 'resume' | 'expire') => {
    try {
      if (action === 'suspend') await hq.tenant.suspend(row.id);
      else if (action === 'resume') await hq.tenant.resume(row.id);
      else await hq.tenant.expire(row.id);
      message.success(action === 'suspend' ? '已停用' : action === 'resume' ? '已恢复' : '已置为到期');
      // 列表 + 抽屉内的机构/管理员/运营中心/用量都要失效，否则详情仍显示旧状态
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['tenants'] }),
        queryClient.invalidateQueries({
          predicate: (q) =>
            typeof q.queryKey[0] === 'string' && (q.queryKey[0] as string).startsWith('tenant-')
        })
      ]);
    } catch (err) {
      message.error(err instanceof ApiError ? err.friendlyMessage : String(err));
    }
  };

  // ---------------- 进入租户视角（docs/07 §7.4 enter）----------------

  const [enteringId, setEnteringId] = useState<string | null>(null);

  const enterTenant = async (row: TenantRow) => {
    setEnteringId(row.id);
    try {
      const result = await hq.tenant.enter(row.id);
      // 令牌切换后当前页面所有平台接口都会 403，故立即跳转到代管可用的机构页
      startEnter({
        token: result.token,
        tenantId: result.tenantId,
        tenantName: row.tenantName,
        minutes: result.expiresInMinutes
      });
      message.success(`已进入「${row.tenantName}」代管视角（只读 ${result.expiresInMinutes} 分钟）`);
      await queryClient.invalidateQueries();
      navigate('/system/orgs', { replace: true });
    } catch (err) {
      message.error(err instanceof ApiError ? err.friendlyMessage : String(err));
    } finally {
      setEnteringId(null);
    }
  };

  // ---------------- 续约（docs/07 §7.4 renew）----------------

  const openRenew = (row: TenantRow) => {
    setRenewTarget(row);
    // 默认在当前到期日基础上加一年，运营方只需微调
    setRenewEnd(row.serviceEnd ? dayjs(row.serviceEnd).add(1, 'year') : dayjs().add(1, 'year'));
  };

  const submitRenew = async () => {
    if (!renewTarget || !renewEnd) {
      message.warning('请选择新的服务到期日');
      return;
    }
    setRenewing(true);
    try {
      await hq.tenant.renew(renewTarget.id, { serviceEnd: renewEnd.endOf('day').toISOString() });
      message.success('续约成功，租户已恢复为运行中');
      setRenewTarget(null);
      await queryClient.invalidateQueries({ queryKey: ['tenants'] });
    } catch (err) {
      message.error(err instanceof ApiError ? err.friendlyMessage : String(err));
    } finally {
      setRenewing(false);
    }
  };

  // ---------------- 运营端（OPS）管理员 ----------------

  const openDetail = (row: TenantRow, tab: DetailTab = 'orgs') => {
    setDetailTab(tab);
    setChecking(row);
  };

  // 租户编码由系统生成 = 行政区划代码：表单只读回显，跟选区划联动
  const regionCodeWatch = Form.useWatch('regionCode', form) as string | undefined;

  // 运营管理员账号由系统生成（机构号 + 4 位序号）：新增时只读回显服务端将生成的登录名
  const adminPreflight = useQuery({
    queryKey: ['tenant-admin-preflight', checking?.id],
    queryFn: () => hq.tenant.adminPreflight(checking?.id ?? ''),
    enabled: adminOpen && !adminEditing && Boolean(checking?.id),
    staleTime: 5 * 1000
  });

  const openAdminCreate = () => {
    setAdminEditing(null);
    adminForm.resetFields();
    // 缺省挂到该租户首个运营中心：运营端很多功能按 ops_center_id 取数
    adminForm.setFieldsValue({
      status: 'ENABLED',
      opsCenterId: detail.data?.centers[0]?.id
    });
    setAdminOpen(true);
  };

  const openAdminEdit = (row: TenantAdminRow) => {
    setAdminEditing(row);
    adminForm.setFieldsValue({
      realName: row.realName,
      mobile: row.mobile ?? undefined,
      password: undefined,
      opsCenterId: row.opsCenterId && row.opsCenterId !== '0' ? row.opsCenterId : undefined,
      status: row.status === 'DISABLED' ? 'DISABLED' : 'ENABLED'
    });
    setAdminOpen(true);
  };

  const invalidateAdmins = async () => {
    if (!checking) return;
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['tenant-admins', checking.id] }),
      queryClient.invalidateQueries({ queryKey: ['tenant-admin-preflight', checking.id] }),
      queryClient.invalidateQueries({ queryKey: ['tenants'] })
    ]);
  };

  const submitAdmin = async () => {
    if (!checking) return;
    const values = await adminForm.validateFields();
    setAdminSaving(true);
    try {
      const saved = await hq.tenant.saveAdmin(checking.id, {
        id: adminEditing?.id,
        // 账号由服务端按「机构号 + 4 位序号」生成，前端不提交
        realName: values.realName?.trim() || undefined,
        password: values.password ? values.password : undefined,
        mobile: values.mobile?.trim() || undefined,
        opsCenterId: values.opsCenterId,
        status: values.status
      });
      if (adminEditing) {
        message.success('运营管理员已更新');
      } else {
        // 账号由系统生成，只在创建结果里出现这一次：常驻弹窗回显，由用户手工关闭
        Modal.success({
          title: '运营管理员已创建',
          width: 480,
          okText: '我已记录，关闭',
          content: (
            <div style={{ lineHeight: 1.9 }}>
              <div>
                登录账号：
                <span style={{ fontFamily: 'monospace', fontWeight: 600, fontSize: 15 }}>
                  {saved.account}
                </span>
              </div>
              <div style={{ color: 'rgba(0,0,0,0.45)' }}>
                该账号仅本次回显，请立即记录并转交；口令为本次填写的初始口令，可用于登录运营端。
              </div>
            </div>
          )
        });
      }
      setAdminOpen(false);
      await invalidateAdmins();
    } catch (err) {
      message.error(err instanceof ApiError ? err.friendlyMessage : String(err));
    } finally {
      setAdminSaving(false);
    }
  };

  // ---------------- 运营中心（租户详情 → 运营中心 Tab）----------------

  const invalidateCenters = async () => {
    if (!checking) return;
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['tenant-centers', checking.id] }),
      queryClient.invalidateQueries({ queryKey: ['tenant-orgs', checking.id] }),
      queryClient.invalidateQueries({ queryKey: ['tenant-metric', checking.id] }),
      queryClient.invalidateQueries({ queryKey: ['tenants'] })
    ]);
  };

  const openCenter = (row?: TenantCenterRow) => {
    setCenterEditing(row ?? null);
    centerForm.resetFields();
    centerForm.setFieldsValue({
      centerName: row?.centerName ?? undefined,
      centerShortName: row?.centerShortName ?? undefined,
      leaderName: row?.leaderName ?? undefined,
      leaderPhone: row?.leaderPhone ?? undefined,
      address: row?.address ?? undefined,
      status: (row?.status as 'ACTIVE' | 'INACTIVE' | undefined) ?? 'ACTIVE'
    });
    setCenterOpen(true);
  };

  const submitCenter = async () => {
    if (!checking) return;
    const values = await centerForm.validateFields();
    setCenterSaving(true);
    try {
      await hq.tenant.saveCenter(checking.id, {
        id: centerEditing?.id,
        // 中心编码由服务端按「租户编码-OC-序号」生成，前端不提交
        centerName: values.centerName?.trim(),
        centerShortName: values.centerShortName?.trim(),
        leaderName: values.leaderName?.trim() || undefined,
        leaderPhone: values.leaderPhone?.trim() || undefined,
        address: values.address?.trim() || undefined,
        status: values.status
      });
      message.success(centerEditing ? '运营中心已更新' : '运营中心已新增');
      setCenterOpen(false);
      await invalidateCenters();
    } catch (err) {
      message.error(err instanceof ApiError ? err.friendlyMessage : String(err));
    } finally {
      setCenterSaving(false);
    }
  };

  const removeCenter = async (row: TenantCenterRow) => {
    if (!checking) return;
    try {
      await hq.tenant.removeCenter(checking.id, row.id);
      message.success('运营中心已删除');
      await invalidateCenters();
    } catch (err) {
      message.error(err instanceof ApiError ? err.friendlyMessage : String(err));
    }
  };

  const centerColumns: ColumnsType<TenantCenterRow> = [
    { title: '中心编码', dataIndex: 'centerCode', width: 150 },
    {
      title: '名称',
      dataIndex: 'centerName',
      ellipsis: { showTitle: false },
      render: (v: string) => <TipText text={v} />
    },
    { title: '简称', dataIndex: 'centerShortName', width: 110, render: (v: string | null) => v ?? '-' },
    { title: '联系人', dataIndex: 'leaderName', width: 110, render: (v: string | null) => v ?? '-' },
    { title: '联系电话', dataIndex: 'leaderPhone', width: 130, render: (v: string | null) => v ?? '-' },
    {
      title: '状态',
      dataIndex: 'status',
      width: 90,
      render: (v: string) => (v === 'ACTIVE' ? <Tag color="green">启用</Tag> : <Tag>停用</Tag>)
    },
    {
      title: '操作',
      key: 'action',
      width: 130,
      render: (_: unknown, row) => (
        <Space size={4}>
          <Button size="small" disabled={!canOpsCenter} onClick={() => openCenter(row)}>
            编辑
          </Button>
          <Popconfirm
            title="删除后不可恢复；仍有账号归属时会被拒绝"
            onConfirm={() => void removeCenter(row)}
          >
            <Button size="small" danger disabled={!canOpsCenter}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  const toggleAdmin = async (row: TenantAdminRow) => {
    if (!checking) return;
    const next = row.status === 'ENABLED' ? 'DISABLED' : 'ENABLED';
    try {
      await hq.tenant.saveAdmin(checking.id, { id: row.id, status: next });
      message.success(next === 'ENABLED' ? '已启用该运营管理员' : '已停用该运营管理员');
      await invalidateAdmins();
    } catch (err) {
      message.error(err instanceof ApiError ? err.friendlyMessage : String(err));
    }
  };

  const adminColumns: ColumnsType<TenantAdminRow> = [
    {
      title: '登录账号',
      dataIndex: 'account',
      width: 150,
      render: (value: string, row) => (
        // 单行展示：真实姓名收进悬浮提示，避免行高被拉高
        <Tooltip placement="topLeft" title={row.realName ? `姓名：${row.realName}` : undefined}>
          <Typography.Text strong>{value}</Typography.Text>
        </Tooltip>
      )
    },
    { title: '手机号', dataIndex: 'mobile', width: 130, render: (v: string | null) => v ?? '-' },
    {
      title: '归属运营中心',
      dataIndex: 'opsCenterName',
      width: 180,
      ellipsis: { showTitle: false },
      render: (v: string | null, row) =>
        v ? <TipText text={v} /> : <Typography.Text type="warning">未绑定（{row.opsCenterId}）</Typography.Text>
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 90,
      render: (value: string) =>
        value === 'ENABLED' ? <Tag color="green">启用</Tag> : <Tag color="red">停用</Tag>
    },
    {
      title: '最近登录',
      dataIndex: 'lastLoginAt',
      width: 150,
      render: (v: string | null) => (v ? formatDisplayTime(v) : '从未登录')
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      fixed: 'right',
      render: (_, row) => (
        <Space size={4}>
          <Button size="small" disabled={!canManage} onClick={() => openAdminEdit(row)}>
            编辑
          </Button>
          <Button size="small" danger={row.status === 'ENABLED'} disabled={!canManage} onClick={() => void toggleAdmin(row)}>
            {row.status === 'ENABLED' ? '停用' : '启用'}
          </Button>
        </Space>
      )
    }
  ];

  // 列定义抽成常量：文本列统一 ellipsis + antd Tooltip（避免原生 title 浮层错位），并用 sumWidth 核算横向宽度
  const tenantColumns: ColumnsType<TenantRow> = [
    { title: '租户编码', dataIndex: 'tenantCode', width: 150, ellipsis: { showTitle: false }, render: (v: string) => <TipText text={v} /> },
    { title: '租户名称', dataIndex: 'tenantName', width: 220, ellipsis: { showTitle: false }, render: (v: string) => <TipText text={v} /> },
    { title: '行政区划', dataIndex: 'regionName', width: 150, ellipsis: { showTitle: false }, render: (v: string | null, row) => <TipText text={v ?? row.regionCode} /> },
    { title: '监管单位', dataIndex: 'superviseOrgName', width: 220, ellipsis: { showTitle: false }, render: (v: string | null) => (v ? <TipText text={v} /> : '-') },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: (value: TenantStatus) => {
        const meta = STATUS_META[value] ?? { color: 'default', label: value };
        return <Tag color={meta.color}>{meta.label}</Tag>;
      }
    },
    {
      title: '服务期',
      width: 200,
      render: (_v, row) =>
        row.serviceBegin && row.serviceEnd
          ? `${formatDisplayDate(row.serviceBegin)} ~ ${formatDisplayDate(row.serviceEnd)}`
          : '未约定'
    },
    {
      title: '配额（机构/账号/中心）',
      width: 200,
      render: (_v, row) => `${row.maxOrgCount} / ${row.maxUserCount} / ${row.maxOpsCenter}`
    },
    {
      // 实际规模 vs 配额：只列上限无法判断是否已接近限额（配额执行只在新建立时校验）
      title: '实际（机构/账号/中心）',
      width: 200,
      render: (_v, row) => `${row.orgCount ?? 0} / ${row.userCount ?? 0} / ${row.opsCenterCount ?? 0}`
    },
    {
      title: '操作',
      width: 380,
      fixed: 'right',
      render: (_v, row) => (
        <Space size={4}>
          <Button size="small" disabled={!canManage} onClick={() => openEdit(row)}>
            编辑
          </Button>
          <Button size="small" onClick={() => openDetail(row, 'admins')}>
            运营管理员
          </Button>
          <Button size="small" onClick={() => openDetail(row, 'orgs')}>
            机构核对
          </Button>
          <Button size="small" onClick={() => openDetail(row, 'centers')}>
            运营中心
          </Button>
          <Popconfirm
            title="进入租户视角"
            description={`将以只读方式查看「${row.tenantName}」的机构/仓库/用户数据，15 分钟后自动退出`}
            onConfirm={() => void enterTenant(row)}
          >
            <Button size="small" loading={enteringId === row.id} disabled={!canManage || row.status !== 'RUNNING'}>
              进入视角
            </Button>
          </Popconfirm>
          <Button size="small" disabled={!canLifecycle} onClick={() => openRenew(row)}>
            续约
          </Button>
          {row.status === 'SUSPEND' ? (
            <Button
              size="small"
              type="primary"
              ghost
              disabled={!canLifecycle}
              onClick={() => void changeStatus(row, 'resume')}
            >
              恢复
            </Button>
          ) : (
            <Popconfirm
              title="停用后该租户所有账号将无法登录"
              onConfirm={() => void changeStatus(row, 'suspend')}
            >
              <Button size="small" danger disabled={!canLifecycle}>
                停用
              </Button>
            </Popconfirm>
          )}
          {row.status === 'EXPIRED' ? null : (
            <Popconfirm
              title="置为到期"
              description="该租户全部会话会立即失效，且需续约后才能恢复；用于服务到期未续约的强制处置"
              onConfirm={() => void changeStatus(row, 'expire')}
            >
              <Button size="small" danger type="text" disabled={!canLifecycle}>
                置为到期
              </Button>
            </Popconfirm>
          )}
        </Space>
      )
    }
  ];

  return (
    <div>
      <Typography.Title level={4} style={{ marginTop: 0 }}>
        租户管理
      </Typography.Title>
      <Typography.Paragraph type="secondary">
        一行政区 = 一租户（DR-8.11）；开通会同步创建监管单位、运营中心与首个平台管理员。跨租户接口需 SYSTEM 门户。
      </Typography.Paragraph>

      {query.error ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={query.error instanceof ApiError ? query.error.friendlyMessage : String(query.error)}
          description="若提示无权访问，请使用 SYSTEM 门户账号（如 t_platform）登录。"
        />
      ) : null}

      <Tabs
        items={[
          {
            key: 'tenants',
            label: '租户列表',
            children: (
              <>
                <Card size="small" style={{ marginBottom: 16 }}>
                  <Space wrap>
                    <Input.Search
                      allowClear
                      placeholder="租户名称 / 编码"
                      style={{ width: 240 }}
                      onSearch={(value) => {
                        setPage(1);
                        setKeyword(value.trim());
                      }}
                    />
                    <Select
                      allowClear
                      placeholder="状态"
                      style={{ width: 140 }}
                      value={status || undefined}
                      onChange={(value) => {
                        setPage(1);
                        setStatus((value as TenantStatus) ?? '');
                      }}
                      options={Object.entries(STATUS_META).map(([value, meta]) => ({
                        value,
                        label: meta.label
                      }))}
                    />
                    <Button onClick={() => void query.refetch()} loading={query.isFetching}>
                      刷新
                    </Button>
                    <Button type="primary" disabled={!canManage} onClick={openCreate}>
                      开通租户
                    </Button>
                  </Space>
                </Card>

                <Table<TenantRow>
                  size="small"
                  rowKey="id"
                  loading={query.isLoading}
                  dataSource={tenantRows}
                  scroll={{ x: sumWidth(tenantColumns) }}
                  columns={tenantColumns}
                  pagination={false}
                />
                <Space style={{ marginTop: 12 }}>
                  <Button size="small" disabled={page <= 1} onClick={() => setPage((value) => Math.max(1, value - 1))}>
                    上一页
                  </Button>
                  <Typography.Text type="secondary">第 {page} 页</Typography.Text>
                  <Button size="small" disabled={!hasNextPage} onClick={() => setPage((value) => value + 1)}>
                    下一页
                  </Button>
                  <Typography.Text type="secondary">每页 {PAGE_SIZE} 条</Typography.Text>
                </Space>
              </>
            )
          },
          {
            // 区划字典已升级为「全国行政区划」数据源（导入 + 树形浏览 + 纠错），
            // 与侧边栏「行政区划」页共用同一实现，避免两处维护口径不一致
            key: 'regions',
            label: '行政区划',
            children: <Regions />
          }
        ]}
      />

      <Drawer
        title={editing ? `编辑租户 · ${editing.tenantName}` : '开通租户'}
        width={560}
        open={formOpen}
        onClose={() => setFormOpen(false)}
        destroyOnHidden
        extra={
          <Button type="primary" loading={submitting} disabled={!canManage} onClick={() => form.submit()}>
            保存
          </Button>
        }
      >
        <Form<TenantFormValues> form={form} layout="vertical" onFinish={(values) => void submit(values)}>
          {editing ? null : (
            <>
              <Form.Item label="租户编码" extra="由系统生成 = 行政区划代码，不可修改（也是监管单位机构号前缀）">
                <Input readOnly placeholder="选择行政区划后自动带出" value={regionCodeWatch ?? ''} />
              </Form.Item>
              <Form.Item name="tenantName" label="租户名称" rules={[{ required: true, message: '请输入租户名称' }]}>
                <Input placeholder="如 杭州市卫生健康委" />
              </Form.Item>
              <Form.Item name="regionCode" label="行政区划" rules={[{ required: true, message: '请选择区划' }]}>
                <Select showSearch optionFilterProp="label" options={regionOptions} placeholder="选择行政区划" />
              </Form.Item>
              <Form.Item name="superviseOrgName" label="监管单位名称" extra="留空自动生成「{租户名称}卫生健康局」">
                <Input />
              </Form.Item>
            </>
          )}

          {editing ? (
            <>
              <Form.Item name="tenantName" label="租户名称">
                <Input />
              </Form.Item>
              <Form.Item name="superviseOrgId" label="监管单位" extra="从本租户名下机构中选择">
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder="搜索并选择机构"
                  options={(superviseOrgs.data ?? []).map((org) => ({
                    value: String(org.id),
                    label: `${org.orgName}（${org.orgCode}）`
                  }))}
                />
              </Form.Item>
            </>
          ) : null}

          <Form.Item name="contractNo" label="合同编号">
            <Input />
          </Form.Item>
          <Form.Item name="serviceRange" label="服务期">
            <DatePicker.RangePicker style={{ width: '100%' }} />
          </Form.Item>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="maxOrgCount" label="机构数上限">
                <InputNumber min={1} max={9999} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="maxUserCount" label="账号数上限">
                <InputNumber min={1} max={999999} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="maxOpsCenter" label="运营中心上限">
                <InputNumber min={1} max={100} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="storageQuotaMb" label="存储配额（MB）">
                <InputNumber min={1024} max={1048576} step={1024} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          {editing ? null : (
            <>
              <Divider orientation="left" plain>
                运营中心（可选）
              </Divider>
              <Row gutter={12}>
                <Col span={10}>
                  <Form.Item label="运营中心编码" extra="由系统生成「租户编码-OC-序号」">
                    <Input readOnly value="开通后自动生成" />
                  </Form.Item>
                </Col>
                <Col span={9}>
                  <Form.Item
                    name="centerName"
                    label="运营中心名称"
                    rules={[
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          // 填了名称即视为要建运营中心，此时简称/LOGO/地址必须补齐
                          if (value) return Promise.resolve();
                          const rest = ['centerShortName', 'centerLogoUrl', 'address'];
                          const filled = rest.some((key) => Boolean(getFieldValue(key)));
                          return filled
                            ? Promise.reject(new Error('已填写运营中心信息，请补齐名称'))
                            : Promise.resolve();
                        }
                      })
                    ]}
                  >
                    <Input placeholder="如 杭州区域运营中心" />
                  </Form.Item>
                </Col>
                <Col span={5}>
                  <Form.Item name="centerType" label="类型" initialValue="REGION">
                    <Select
                      options={[
                        { value: 'REGION', label: '区域中心' },
                        { value: 'HYBRID', label: '混合中心' },
                        { value: 'ORG', label: '院内中心' }
                      ]}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="centerShortName"
                    label="运营中心简称（2–6 个汉字）"
                    rules={[
                      ({ getFieldValue }) => ({
                        validator(_, value: string) {
                          if (!getFieldValue('centerName')) return Promise.resolve();
                          const error = checkShortName(value);
                          return error ? Promise.reject(new Error(error)) : Promise.resolve();
                        }
                      })
                    ]}
                  >
                    <Input placeholder="如 杭州中心" maxLength={6} showCount />
                  </Form.Item>
                </Col>
              </Row>

              {hasCenter ? (
                <>
                  <LogoField
                    hq={hq}
                    form={form}
                    name="centerLogoUrl"
                    label="运营中心 LOGO"
                    required
                    bizType="OPS_CENTER_LOGO"
                  />
                  <RegionAddressFields
                    hq={hq}
                    form={form}
                    required
                    addressHint="运营仓地址用于配送范围与运费计算，省级/市级/区县缺省跟随租户所属区划"
                  />
                </>
              ) : (
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  填写运营中心名称后可登记简称、LOGO 与省市区地址（运营仓定位所需）。
                </Typography.Text>
              )}

              <Divider orientation="left" plain>
                运营端管理员（OPS，必填）
              </Divider>
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 12 }}
                message="该账号用于登录运营端（OPS），开通后由平台侧维护"
                description="登录名全平台唯一；创建后自动绑定运营端管理员角色，并挂到本租户首个运营中心，运营端的管理功能（机构/仓库/账号）依赖它。"
              />
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item
                    label="登录账号"
                    extra="由系统按「监管单位机构号 + 4 位序号」生成，开通后回显"
                  >
                    <Input readOnly value="开通后自动生成" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="adminRealName" label="姓名">
                    <Input maxLength={32} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="adminMobile"
                    label="手机号"
                    rules={[{ pattern: /^1[3-9]\d{9}$/, message: '手机号格式不正确' }]}
                    extra="选填，可用于手机号登录"
                  >
                    <Input maxLength={11} placeholder="11 位手机号" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="adminPassword"
                    label="初始口令"
                    rules={[
                      { required: true, message: '请设置初始口令' },
                      { min: 8, message: '口令不少于 8 位' }
                    ]}
                    extra="建议 8 位以上，含大小写与数字"
                  >
                    <Input.Password autoComplete="new-password" />
                  </Form.Item>
                </Col>
              </Row>

              <Divider orientation="left" plain>
                模板套用（可选，docs/07 §7.3 #5 步骤 ⑥）
              </Divider>
              <Form.Item
                name="roleTemplates"
                label="角色模板"
                extra="按模板在本租户建角色（编码自动加租户前缀），并绑定模板里的权限码"
              >
                <Select
                  mode="multiple"
                  allowClear
                  optionFilterProp="label"
                  placeholder="选择角色模板"
                  options={(roleTplQuery.data ?? []).map((row: TplRow) => ({
                    value: row.tplCode,
                    label: `${row.tplName}（${row.tplCode}）`
                  }))}
                />
              </Form.Item>
              <Form.Item
                name="approvalTemplates"
                label="审批流模板"
                extra="按模板建审批流与节点；模板 payload.active=true 时直接 ACTIVE，否则为 DRAFT"
              >
                <Select
                  mode="multiple"
                  allowClear
                  optionFilterProp="label"
                  placeholder="选择审批流模板"
                  options={(approvalTplQuery.data ?? []).map((row: TplRow) => ({
                    value: row.tplCode,
                    label: `${row.tplName}（${row.tplCode}）`
                  }))}
                />
              </Form.Item>
              <Form.Item
                name="dictTemplates"
                label="字典模板"
                extra="按模板建租户字典与字典项（编码加租户前缀，如 URGENCY → 330100_URGENCY）"
              >
                <Select
                  mode="multiple"
                  allowClear
                  optionFilterProp="label"
                  placeholder="选择字典模板"
                  options={(dictTplQuery.data ?? []).map((row: TplRow) => ({
                    value: row.tplCode,
                    label: `${row.tplName}（${row.tplCode}）`
                  }))}
                />
              </Form.Item>
              <Form.Item
                name="numberRuleTemplates"
                label="编号规则模板"
                extra="按模板建单号规则（落在本租户监管机构的 org_id 上，biz_type 保持模板原值）"
              >
                <Select
                  mode="multiple"
                  allowClear
                  optionFilterProp="label"
                  placeholder="选择编号规则模板"
                  options={(numberRuleTplQuery.data ?? []).map((row: TplRow) => ({
                    value: row.tplCode,
                    label: `${row.tplName}（${row.tplCode}）`
                  }))}
                />
              </Form.Item>
            </>
          )}

          <Form.Item name="remark" label="备注">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Drawer>

      <Drawer
        title={checking ? `租户详情 · ${checking.tenantName}` : '租户详情'}
        width={880}
        open={Boolean(checking)}
        onClose={() => setChecking(null)}
        extra={
          detailTab === 'admins' ? (
            <Button type="primary" disabled={!canManage} onClick={openAdminCreate}>
              新增运营管理员
            </Button>
          ) : null
        }
      >
        <Tabs
          activeKey={detailTab}
          onChange={(key) => setDetailTab(key as DetailTab)}
          items={[
            {
              key: 'orgs',
              label: '机构与运营中心',
              children: (
                <>
                  {detail.error ? (
                    <Alert
                      type="error"
                      showIcon
                      style={{ marginBottom: 12 }}
                      message={
                        detail.error instanceof ApiError
                          ? detail.error.friendlyMessage
                          : String(detail.error)
                      }
                    />
                  ) : null}
                  <Descriptions size="small" column={2} style={{ marginBottom: 16 }}>
                    <Descriptions.Item label="机构数">{detail.data?.orgs.length ?? 0}</Descriptions.Item>
                    <Descriptions.Item label="运营中心">{detail.data?.centers.length ?? 0}</Descriptions.Item>
                  </Descriptions>
                  <Typography.Text strong>机构</Typography.Text>
                  <Table
                    size="small"
                    rowKey="id"
                    loading={detail.isLoading}
                    pagination={false}
                    style={{ marginTop: 8, marginBottom: 16 }}
                    dataSource={detail.data?.orgs ?? []}
                    columns={[
                      { title: '机构编码', dataIndex: 'orgCode', width: 140 },
                      {
                        title: '机构名称',
                        dataIndex: 'orgName',
                        ellipsis: { showTitle: false },
                        render: (v: string) => <TipText text={v} />
                      },
                      { title: '类型', dataIndex: 'instType', width: 120 },
                      { title: '层级', dataIndex: 'levelNo', width: 70 }
                    ]}
                  />
                  <Typography.Text strong>运营中心</Typography.Text>
                  <Table
                    size="small"
                    rowKey="id"
                    loading={detail.isLoading}
                    pagination={false}
                    style={{ marginTop: 8 }}
                    dataSource={detail.data?.centers ?? []}
                    columns={[
                      { title: '中心编码', dataIndex: 'centerCode', width: 140 },
                      {
                        title: '名称',
                        dataIndex: 'centerName',
                        ellipsis: { showTitle: false },
                        render: (v: string) => <TipText text={v} />
                      },
                      { title: '服务范围', dataIndex: 'serveType', width: 120 }
                    ]}
                  />
                </>
              )
            },
            {
              key: 'centers',
              label: '运营中心',
              children: (
                <>
                  <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message="一个租户可有多家运营中心（区域仓运营主体），新建受 max_ops_center 配额约束"
                    description="开通向导只登记首个运营中心；后续在此增补。仍有账号归属的运营中心不能删除，请先调整人员归属。"
                  />
                  {centersQuery.error ? (
                    <Alert
                      type="error"
                      showIcon
                      style={{ marginBottom: 12 }}
                      message={
                        centersQuery.error instanceof ApiError
                          ? centersQuery.error.friendlyMessage
                          : String(centersQuery.error)
                      }
                    />
                  ) : null}
                  <Space style={{ marginBottom: 12 }}>
                    <Button type="primary" disabled={!canOpsCenter} onClick={() => openCenter()}>
                      新增运营中心
                    </Button>
                    <Typography.Text type="secondary">
                      共 {centersQuery.data?.length ?? 0} 个
                    </Typography.Text>
                  </Space>
                  <Table<TenantCenterRow>
                    size="small"
                    rowKey="id"
                    loading={centersQuery.isLoading}
                    pagination={false}
                    dataSource={centersQuery.data ?? []}
                    scroll={{ x: sumWidth(centerColumns) }}
                    columns={centerColumns}
                  />
                </>
              )
            },
            {
              key: 'sequences',
              label: '编号规则',
              children: (
                <>
                  <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message="编号规则预览"
                    description="按该租户当前模板推算下一条样号，仅展示不占用序号；规则模板在「平台基础数据 → 编号规则」维护。"
                  />
                  {sequenceQuery.error ? (
                    <Alert
                      type="error"
                      showIcon
                      style={{ marginBottom: 12 }}
                      message={
                        sequenceQuery.error instanceof ApiError
                          ? sequenceQuery.error.friendlyMessage
                          : String(sequenceQuery.error)
                      }
                    />
                  ) : null}
                  <Table<TenantSequencePreview>
                    size="small"
                    rowKey="bizType"
                    loading={sequenceQuery.isLoading}
                    pagination={false}
                    dataSource={sequenceQuery.data ?? []}
                    columns={[
                      { title: '单据类型', dataIndex: 'bizType', width: 160 },
                      { title: '前缀', dataIndex: 'prefix', width: 100 },
                      { title: '日期段', dataIndex: 'dateFormat', width: 110 },
                      { title: '当前序号', dataIndex: 'currentNo', width: 110, align: 'right' },
                      { title: '规则', dataIndex: 'formatHint' },
                      {
                        title: '下一条样号',
                        dataIndex: 'sample',
                        render: (value: string) => <Typography.Text code>{value || '-'}</Typography.Text>
                      }
                    ]}
                  />
                </>
              )
            },
            {
              key: 'quota',
              label: '配额与日志',
              children: (
                <>
                  <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message="配额用量与最近操作日志"
                    description="配额来自 sys_tenant（0 = 不限）；日志按 tenant_id 过滤，最近 10 条。"
                  />
                  {metricQuery.error ? (
                    <Alert
                      type="error"
                      showIcon
                      style={{ marginBottom: 12 }}
                      message={
                        metricQuery.error instanceof ApiError
                          ? metricQuery.error.friendlyMessage
                          : String(metricQuery.error)
                      }
                    />
                  ) : null}
                  {(() => {
                    const metric: TenantMetricRow | undefined = metricQuery.data?.[0];
                    const ratio = (used: number, max: number) =>
                      max > 0 ? Math.min(100, Math.round((used / max) * 100)) : 0;
                    return (
                      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                        <Col xs={12} md={6}>
                          <Card size="small" loading={metricQuery.isLoading}>
                            <Statistic
                              title="机构数"
                              value={metric?.orgCount ?? 0}
                              suffix={`/ ${metric?.maxOrgCount ? metric.maxOrgCount : '不限'}`}
                            />
                            {metric && metric.maxOrgCount > 0 ? (
                              <Progress
                                size="small"
                                percent={ratio(metric.orgCount, metric.maxOrgCount)}
                                status={metric.orgCount >= metric.maxOrgCount ? 'exception' : 'normal'}
                              />
                            ) : null}
                          </Card>
                        </Col>
                        <Col xs={12} md={6}>
                          <Card size="small" loading={metricQuery.isLoading}>
                            <Statistic
                              title="账号数"
                              value={metric?.userCount ?? 0}
                              suffix={`/ ${metric?.maxUserCount ? metric.maxUserCount : '不限'}`}
                            />
                            {metric && metric.maxUserCount > 0 ? (
                              <Progress
                                size="small"
                                percent={ratio(metric.userCount, metric.maxUserCount)}
                                status={metric.userCount >= metric.maxUserCount ? 'exception' : 'normal'}
                              />
                            ) : null}
                          </Card>
                        </Col>
                        <Col xs={12} md={6}>
                          <Card size="small" loading={metricQuery.isLoading}>
                            <Statistic
                              title="运营中心"
                              value={metric?.opsCenterCount ?? 0}
                              suffix={`/ ${metric?.maxOpsCenter ? metric.maxOpsCenter : '不限'}`}
                            />
                            {metric && metric.maxOpsCenter > 0 ? (
                              <Progress
                                size="small"
                                percent={ratio(metric.opsCenterCount, metric.maxOpsCenter)}
                                status={metric.opsCenterCount >= metric.maxOpsCenter ? 'exception' : 'normal'}
                              />
                            ) : null}
                          </Card>
                        </Col>
                        <Col xs={12} md={6}>
                          <Card size="small" loading={metricQuery.isLoading}>
                            <Statistic
                              title="存储用量"
                              value={Math.round(((metric?.storageUsedBytes ?? 0) / 1024 / 1024) * 10) / 10}
                              suffix={`MB / ${metric?.storageQuotaMb ? `${metric.storageQuotaMb} MB` : '不限'}`}
                            />
                            {metric && metric.storageQuotaMb > 0 ? (
                              <Progress
                                size="small"
                                percent={Math.min(
                                  100,
                                  Math.round((metric.storageUsedBytes / 1024 / 1024 / metric.storageQuotaMb) * 100)
                                )}
                                status={
                                  metric.storageUsedBytes / 1024 / 1024 / metric.storageQuotaMb >= 0.8
                                    ? 'exception'
                                    : 'normal'
                                }
                              />
                            ) : null}
                          </Card>
                        </Col>
                      </Row>
                    );
                  })()}

                  <Typography.Text strong>最近操作日志</Typography.Text>
                  {!canViewAudit ? (
                    <Alert
                      type="info"
                      showIcon
                      style={{ marginTop: 8 }}
                      message="无审计日志查看权限"
                      description="需要权限码 sys:audit:view 或 sys:platform:audit。"
                    />
                  ) : tenantLogs.error ? (
                    <Alert
                      type="warning"
                      showIcon
                      style={{ marginTop: 8 }}
                      message={
                        tenantLogs.error instanceof ApiError
                          ? tenantLogs.error.friendlyMessage
                          : String(tenantLogs.error)
                      }
                      description="查看审计日志需要权限码 sys:audit:view（或 sys:platform:audit）。"
                    />
                  ) : (
                    <Table<AuditLogRow>
                      size="small"
                      rowKey="id"
                      loading={tenantLogs.isLoading}
                      pagination={false}
                      style={{ marginTop: 8 }}
                      dataSource={tenantLogs.data?.items ?? []}
                      locale={{ emptyText: '该租户暂无审计记录' }}
                      columns={[
                        { title: '时间', dataIndex: 'actAt', width: 170, render: (value: string | null) => formatDisplayTime(value) },
                        { title: '操作', dataIndex: 'action', width: 120, render: (v: string) => <Tag>{v}</Tag> },
                        {
                          title: '业务类型',
                          dataIndex: 'bizType',
                          width: 150,
                          ellipsis: { showTitle: false },
                          render: (v: string | null) => (v ? <TipText text={v} /> : '-')
                        },
                        {
                          title: '操作人',
                          dataIndex: 'userName',
                          width: 140,
                          ellipsis: { showTitle: false },
                          render: (v: string | null, row) => <TipText text={v ?? row.userId} />
                        },
                        {
                          title: '备注',
                          dataIndex: 'remark',
                          ellipsis: { showTitle: false },
                          render: (v: string | null) => (v ? <TipText text={v} /> : '-')
                        }
                      ]}
                    />
                  )}
                </>
              )
            },
            {
              key: 'admins',
              label: `运营管理员（${admins.data?.length ?? 0}）`,
              children: (
                <>
                  <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message="运营端（OPS）管理员：用该账号登录运营端，维护本租户的机构、仓库、账号与业务"
                    description="登录名全平台唯一（不可与其它租户重复）；归属运营中心决定其默认 ops_center_id，未绑定会影响运营中心维度的取数。开通时已自动创建一个，可在此增补或重置口令。"
                  />
                  {admins.error ? (
                    <Alert
                      type="error"
                      showIcon
                      style={{ marginBottom: 12 }}
                      message={
                        admins.error instanceof ApiError
                          ? admins.error.friendlyMessage
                          : String(admins.error)
                      }
                    />
                  ) : null}
                  <Table<TenantAdminRow>
                    size="small"
                    rowKey="id"
                    loading={admins.isLoading}
                    pagination={false}
                    dataSource={admins.data ?? []}
                    scroll={{ x: sumWidth(adminColumns) }}
                    columns={adminColumns}
                  />
                </>
              )
            }
          ]}
        />
      </Drawer>

      <Modal
        title={adminEditing ? `编辑运营管理员 · ${adminEditing.account}` : '新增运营管理员'}
        open={adminOpen}
        onCancel={() => setAdminOpen(false)}
        onOk={() => void submitAdmin()}
        confirmLoading={adminSaving}
        okText="保存"
        width={520}
        destroyOnHidden
      >
        <Form<TenantAdminFormValues> form={adminForm} layout="vertical">
          <Form.Item
            label="登录账号"
            extra={
              adminEditing
                ? '账号全平台唯一，创建后不可修改'
                : '由系统按「监管单位机构号 + 4 位序号」自动生成'
            }
          >
            <Input
              readOnly
              placeholder="保存后自动生成"
              value={adminEditing ? adminEditing.account : adminPreflight.data?.account ?? ''}
            />
          </Form.Item>
          <Form.Item name="realName" label="姓名">
            <Input maxLength={32} />
          </Form.Item>
          <Form.Item
            name="mobile"
            label="手机号"
            rules={[{ pattern: /^1[3-9]\d{9}$/, message: '手机号格式不正确' }]}
            extra="选填，可用于手机号登录"
          >
            <Input maxLength={11} placeholder="11 位手机号" />
          </Form.Item>
          <Form.Item
            name="opsCenterId"
            label="归属运营中心"
            extra="缺省取该租户首个运营中心；运营端按运营中心维度取数"
          >
            <Select
              allowClear
              placeholder="缺省首个运营中心"
              options={(detail.data?.centers ?? []).map((center) => ({
                value: center.id,
                label: `${center.centerName}（${center.centerCode}）`
              }))}
            />
          </Form.Item>
          <Form.Item name="status" label="状态" initialValue="ENABLED">
            <Select
              options={[
                { value: 'ENABLED', label: '启用' },
                { value: 'DISABLED', label: '停用' }
              ]}
            />
          </Form.Item>
          <Form.Item
            name="password"
            label={adminEditing ? '重置口令（留空表示不修改）' : '初始口令'}
            rules={[
              ...(adminEditing ? [] : [{ required: true, message: '请设置初始口令' }]),
              { min: 8, message: '口令不少于 8 位' }
            ]}
            extra="不少于 8 位；保存后 90 天内有效"
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={centerEditing ? `编辑运营中心 · ${centerEditing.centerName}` : '新增运营中心'}
        open={centerOpen}
        onCancel={() => setCenterOpen(false)}
        onOk={() => void submitCenter()}
        confirmLoading={centerSaving}
        okText="保存"
        width={560}
        destroyOnHidden
      >
        <Form<CenterFormValues> form={centerForm} layout="vertical">
          <Form.Item
            label="中心编码"
            extra={centerEditing ? '由系统生成，创建后不可修改' : '由系统生成「租户编码-OC-序号」'}
          >
            <Input readOnly placeholder="保存后自动生成" value={centerEditing?.centerCode ?? ''} />
          </Form.Item>
          <Form.Item name="centerName" label="中心名称" rules={[{ required: true, message: '请输入中心名称' }]}>
            <Input maxLength={64} placeholder="如 富阳区第二运营中心" />
          </Form.Item>
          <Form.Item
            name="centerShortName"
            label="简称（2–6 个汉字）"
            rules={[
              { required: true, message: '请输入简称' },
              {
                validator: (_, value: string) => {
                  const error = checkShortName(value);
                  return error ? Promise.reject(new Error(error)) : Promise.resolve();
                }
              }
            ]}
          >
            <Input maxLength={6} />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="leaderName" label="联系人">
                <Input maxLength={32} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="leaderPhone"
                label="联系电话"
                rules={[{ pattern: /^1[3-9]\d{9}$/, message: '请输入 11 位手机号' }]}
              >
                <Input maxLength={11} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="address" label="地址">
            <Input maxLength={200} />
          </Form.Item>
          <Form.Item name="status" label="状态" initialValue="ACTIVE">
            <Select
              options={[
                { value: 'ACTIVE', label: '启用' },
                { value: 'INACTIVE', label: '停用' }
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={renewTarget ? `续约 · ${renewTarget.tenantName}` : '续约'}
        open={Boolean(renewTarget)}
        onCancel={() => setRenewTarget(null)}
        onOk={() => void submitRenew()}
        confirmLoading={renewing}
        okText="确认续约"
        destroyOnHidden
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="续约会更新服务到期日，并把「已到期/已停用」的租户恢复为运行中"
          description={renewTarget?.serviceEnd ? `当前到期日：${dayjs(renewTarget.serviceEnd).format('YYYY-MM-DD')}` : '当前未设置到期日'}
        />
        <Space direction="vertical" style={{ width: '100%' }}>
          <Typography.Text>新的服务到期日</Typography.Text>
          <DatePicker
            style={{ width: '100%' }}
            value={renewEnd}
            onChange={(value) => setRenewEnd(value)}
            allowClear={false}
          />
        </Space>
      </Modal>

    </div>
  );
}
